import { useSaudiListDateRange } from '../../products/saudi/hooks/useSaudiListDateRange';
﻿import React, { useState, useEffect, useRef, useMemo } from 'react';
import { formatBrandMoney, getBrandDefaultCurrency, getBrandCurrencySuffix, getBrandLocale, getBrandCurrencyOptions } from '../../shared/utils/brandCurrency';
import { useTranslation } from 'react-i18next';
import ReactDOM from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  RefreshCw, Plus, Search, X, Eye, Edit, Trash2,
  DollarSign, Calendar, Filter, ChevronDown, ChevronUp,
  Download, Upload, FileText, FileSpreadsheet, FileOutput,
  List, LayoutList, Layers, CheckSquare, Square,
  Clock, CheckCircle,
  LayoutGrid, Users, XCircle, Building, Repeat, PauseCircle,
  Ban, Send, Shield, Tag
} from 'lucide-react';
import QawaemErpRowActions from '../../products/saudi/components/QawaemErpRowActions';
import QawaemClickableStatusBadge from '../../products/saudi/components/QawaemClickableStatusBadge';
import QawaemTableHeaderActionsCell from '../../products/saudi/components/QawaemTableHeaderActionsCell';
import QawaemTableCheckbox from '../../products/saudi/components/QawaemTableCheckbox';
import { ensureStatusBeforeActions } from '../../shared/utils/erpListColumnOrder';
import { useQawaemTableColumnVisibility, withQawaemActionsColumn, countVisibleTableCols } from '../../shared/hooks/useQawaemTableColumnVisibility';
import localApiAdapter from '../../core/services/localApiAdapter';
import { CustomDatePicker, CustomSelect, CustomNumberInput } from '../../shared/components/custom';
import { exportToCSV, exportToExcel } from '../../shared/utils/importExportUtils';
import FacturationViewModeToggle from './components/FacturationViewModeToggle';
import FacturationListPageLayout, {
  facturationTableHeadClass,
  facturationTableScrollClass,
} from './components/FacturationListPageLayout';
import FacturationListPaginationBar from './components/FacturationListPaginationBar';
import {
  useFacturationListPagination,
  useFacturationListViewMode,
} from './facturationListPageUtils';
import { isSaudiBrand } from '../../products/saudi/utils/isSaudiBrand';
import { useGlobalTheme } from '../../shared/hooks/useGlobalTheme';
import { getErpOverviewGridAttrs } from '../../shared/utils/erpOverviewGridAttrs';
import TableColumnHeaderFilter from '../../shared/components/table/TableColumnHeaderFilter';
import { useColumnHeaderFilters } from '../../shared/hooks/useColumnHeaderFilters';
import { applyColumnFilters, textColumnRule } from '../../shared/utils/columnHeaderFilters';
import QawaemErpCrudModal, { QawaemErpViewModal } from '../../products/saudi/components/QawaemErpCrudModal';
import useFacturationModalBodyLock from './utils/useFacturationModalBodyLock';
import { mergeStickySelectClass } from '../../products/saudi/components/QawaemTableStickyActions';

const EMPTY_FORM = {
  recurring_number: '',
  client_id: '',
  client_name: '',
  frequency: '',
  next_issue_date: '',
  amount: '',
  currency: '',
  template_ref: '',
  auto_send: '',
  status: '',
  notes: ''
};

const FacturesRecurrentesPage = () => {
  const { t, i18n } = useTranslation();
  const dateLocale = getBrandLocale(i18n.language);
  const CURRENCY_OPTIONS = useMemo(() => getBrandCurrencyOptions(), []);
  const p = (key, opts) => t(`modules.facturation.pages.facturesRecurrentesPage.${key}`, opts);
  const { isDashboardLightMode } = useGlobalTheme();

  const FREQUENCY_OPTIONS = useMemo(
    () => [
      { value: 'mensuel', label: p('freq_mensuel'), isDefault: true },
      { value: 'trimestriel', label: p('freq_trimestriel'), isDefault: true },
      { value: 'annuel', label: p('freq_annuel'), isDefault: true },
    ],
    [t],
  );

  const STATUS_OPTIONS = useMemo(
    () => [
      { value: 'active', label: p('status_active'), isDefault: true },
      { value: 'paused', label: p('status_paused'), isDefault: true },
      { value: 'cancelled', label: p('status_cancelled'), isDefault: true },
    ],
    [t],
  );
  const [records, setRecords] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  useFacturationModalBodyLock(showModal, showViewModal);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedRecord, setSelectedRecord] = useState(null);

  const [showAdvancedOptions, setShowAdvancedOptions] = useState(false);
  const [mainViewMode, setMainViewMode] = useState('all_recurring');
  const [quickFilter, setQuickFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [isStatusFilterOpen, setIsStatusFilterOpen] = useState(false);
  const { dateRange, setDateRange } = useSaudiListDateRange();
  const [isDateRangeOpen, setIsDateRangeOpen] = useState(false);
  const [activeDistributionFilter, setActiveDistributionFilter] = useState(null);

  const [showImportDropdown, setShowImportDropdown] = useState(false);
  const [showExportDropdown, setShowExportDropdown] = useState(false);
  const [selectedRecords, setSelectedRecords] = useState([]);
  const { filters, setFilter, editingKey, setEditingKey } = useColumnHeaderFilters();

  const [recurringFormTab, setRecurringFormTab] = useState('client');

  const [formData, setFormData] = useState({ ...EMPTY_FORM });

  const importDropdownRef = useRef(null);
  const exportDropdownRef = useRef(null);
  const statusFilterRef = useRef(null);
  const dateRangeRef = useRef(null);


  useEffect(() => {
    loadRecords();
    loadClients();
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (importDropdownRef.current && !importDropdownRef.current.contains(event.target)) setShowImportDropdown(false);
      if (exportDropdownRef.current && !exportDropdownRef.current.contains(event.target)) setShowExportDropdown(false);
      if (statusFilterRef.current && !statusFilterRef.current.contains(event.target)) setIsStatusFilterOpen(false);
      if (dateRangeRef.current && !dateRangeRef.current.contains(event.target)) setIsDateRangeOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const loadRecords = async () => {
    try {
      setLoading(true);
      const response = await localApiAdapter.get('/facturation/factures-recurrentes');
      setRecords(response?.data || response || []);
    } catch (error) {
      console.error('Error loading recurring invoices:', error);
      setRecords([]);
    } finally {
      setLoading(false);
    }
  };

  const loadClients = async () => {
    try {
      const response = await localApiAdapter.get('/facturation/clients');
      setClients(Array.isArray(response) ? response : response?.data || []);
    } catch (error) {
      console.error('Error loading clients:', error);
      setClients([]);
    }
  };

  const stats = {
    total: records.length,
    actives: records.filter((r) => r.status === 'active').length,
    mensuelles: records.filter((r) => r.frequency === 'mensuel').length,
    trimestrielles: records.filter((r) => r.frequency === 'trimestriel').length,
    caRecurrent: records
      .filter((r) => r.status === 'active')
      .reduce((sum, r) => sum + (parseFloat(r.amount) || 0), 0)
      .toLocaleString(dateLocale, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
  };

  const mainTabs = useMemo(
    () => [
      { id: 'all_recurring', name: p('tab_general'), icon: LayoutGrid },
      { id: 'par_frequence', name: p('tab_by_frequency'), icon: Repeat },
      { id: 'par_statut', name: p('tab_by_status'), icon: Shield },
    ],
    [t],
  );

  const quickFilterButtons = useMemo(
    () => [
      { id: 'all', name: p('filter_all'), icon: Layers },
      { id: 'mensuel', name: p('filter_monthly'), icon: Calendar },
      { id: 'trimestriel', name: p('filter_quarterly'), icon: Clock },
      { id: 'annuel', name: p('filter_annual'), icon: RefreshCw },
    ],
    [t],
  );

  const recurringDistribution = useMemo(
    () => ({
      all_recurring: {
        title: p('overview_title'),
        data: [
          { filterKey: 'active', name: p('dist_active'), count: stats.actives, color: 'bg-gradient-to-r from-green-500 to-green-600' },
          { filterKey: 'mensuel', name: p('dist_monthly'), count: stats.mensuelles, color: 'bg-gradient-to-r from-blue-500 to-blue-600' },
          { filterKey: 'trimestriel', name: p('dist_quarterly'), count: stats.trimestrielles, color: 'bg-gradient-to-r from-purple-500 to-purple-600' },
          {
            filterKey: 'annuel',
            name: p('dist_annual'),
            count: records.filter((r) => r.frequency === 'annuel').length,
            color: 'bg-gradient-to-r from-orange-500 to-orange-600',
          },
        ],
      },
      par_frequence: {
        title: p('dist_by_frequency'),
        data: [
          { filterKey: 'mensuel', name: p('freq_mensuel'), count: stats.mensuelles, color: 'bg-gradient-to-r from-blue-500 to-blue-600' },
          { filterKey: 'trimestriel', name: p('freq_trimestriel'), count: stats.trimestrielles, color: 'bg-gradient-to-r from-purple-500 to-purple-600' },
          {
            filterKey: 'annuel',
            name: p('freq_annuel'),
            count: records.filter((r) => r.frequency === 'annuel').length,
            color: 'bg-gradient-to-r from-orange-500 to-orange-600',
          },
        ],
      },
      par_statut: {
        title: p('dist_by_status'),
        data: [
          { filterKey: 'active', name: p('dist_active'), count: stats.actives, color: 'bg-gradient-to-r from-green-500 to-green-600' },
          {
            filterKey: 'paused',
            name: p('dist_paused'),
            count: records.filter((r) => r.status === 'paused').length,
            color: 'bg-gradient-to-r from-yellow-500 to-yellow-600',
          },
          {
            filterKey: 'cancelled',
            name: p('dist_cancelled'),
            count: records.filter((r) => r.status === 'cancelled').length,
            color: 'bg-gradient-to-r from-red-500 to-red-600',
          },
        ],
      },
    }),
    [stats, records, t],
  );

  const distributionInfo = recurringDistribution[mainViewMode] || recurringDistribution.all_recurring;

  const handleBulkDelete = async () => {
    if (window.confirm(p('bulk_delete_confirm', { count: selectedRecords.length }))) {
      try {
        await Promise.all(selectedRecords.map((id) => localApiAdapter.delete(`/facturation/factures-recurrentes/${id}`)));
        setSelectedRecords([]);
        loadRecords();
      } catch (error) {
        console.error('Error deleting records:', error);
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.client_id && !formData.client_name) {
      window.alert(p('client_required'));
      return;
    }
    try {
      setLoading(true);
      const payload = {
        ...formData,
        client_id: formData.client_id ? parseInt(formData.client_id, 10) : null,
        amount: parseFloat(formData.amount) || 0,
        auto_send: formData.auto_send === '1' || formData.auto_send === true || formData.auto_send === 1
      };
      if (selectedRecord) {
        await localApiAdapter.put(`/facturation/factures-recurrentes/${selectedRecord.id}`, payload);
      } else {
        await localApiAdapter.post('/facturation/factures-recurrentes', payload);
      }
      await loadRecords();
      resetForm();
    } catch (error) {
      console.error('Error saving recurring invoice:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (record) => {
    setSelectedRecord(record);
    const dateStr = record.next_issue_date
      ? typeof record.next_issue_date === 'string'
        ? record.next_issue_date.split('T')[0]
        : new Date(record.next_issue_date).toISOString().split('T')[0]
      : '';
    setFormData({
      recurring_number: record.recurring_number ?? '',
      client_id: record.client_id != null ? String(record.client_id) : '',
      client_name: record.client_name ?? '',
      frequency: record.frequency ?? '',
      next_issue_date: dateStr,
      amount: record.amount ?? '',
      currency: record.currency ?? '',
      template_ref: record.template_ref ?? '',
      auto_send: record.auto_send ? '1' : '',
      status: record.status ?? '',
      notes: record.notes ?? ''
    });
    setRecurringFormTab('client');
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm(p('delete_confirm'))) {
      try {
        setLoading(true);
        await localApiAdapter.delete(`/facturation/factures-recurrentes/${id}`);
        await loadRecords();
      } catch (error) {
        console.error('Error deleting recurring invoice:', error);
      } finally {
        setLoading(false);
      }
    }
  };

  const handleView = (record) => {
    setSelectedRecord(record);
    setShowViewModal(true);
  };

  const resetForm = () => {
    setFormData({ ...EMPTY_FORM });
    setSelectedRecord(null);
    setShowModal(false);
    setRecurringFormTab('client');
  };

  const handleExportCSV = () => {
    const headers = [
      { key: 'recurring_number', label: p('export_col_recurring_number') },
      { key: 'client_name', label: p('export_col_client') },
      { key: 'frequency', label: p('export_col_frequency') },
      { key: 'next_issue_date', label: p('export_col_next_issue') },
      { key: 'amount', label: p('export_col_amount') },
      { key: 'currency', label: p('export_col_currency') },
      { key: 'status', label: p('export_col_status') },
    ];
    const dataToExport = selectedRecords.length > 0
      ? records.filter((r) => selectedRecords.includes(r.id))
      : filteredRecords;
    exportToCSV(dataToExport, headers, `factures-recurrentes-${new Date().toISOString().split('T')[0]}.csv`);
    setShowExportDropdown(false);
  };

  const handleExportExcel = async () => {
    const headers = [
      { key: 'recurring_number', label: p('export_col_recurring_number') },
      { key: 'client_name', label: p('export_col_client') },
      { key: 'frequency', label: p('export_col_frequency') },
      { key: 'next_issue_date', label: p('export_col_next_issue') },
      { key: 'amount', label: p('export_col_amount') },
      { key: 'currency', label: p('export_col_currency') },
      { key: 'status', label: p('export_col_status') },
    ];
    const dataToExport = selectedRecords.length > 0
      ? records.filter((r) => selectedRecords.includes(r.id))
      : filteredRecords;
    await exportToExcel(dataToExport, headers, `factures-recurrentes-${new Date().toISOString().split('T')[0]}.xlsx`, p('export_sheet_name'));
    setShowExportDropdown(false);
  };

  const getFrequencyBadge = (frequency) => {
    const colors = {
      mensuel: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
      trimestriel: 'bg-purple-500/20 text-purple-400 border-purple-500/30',
      annuel: 'bg-orange-500/20 text-orange-400 border-orange-500/30'
    };
    const labels = { mensuel: p('freq_mensuel'), trimestriel: p('freq_trimestriel'), annuel: p('freq_annuel') };
    return (
      <span className={`px-3 py-1.5 rounded-full text-xs font-medium flex items-center gap-1.5 border ${colors[frequency] || 'bg-gray-500/20 text-gray-400 border-gray-500/30'}`}>
        <Repeat size={14} />
        {labels[frequency] || frequency || '-'}
      </span>
    );
  };

  const getStatusBadge = (status) => {
    const config = {
      active: { label: p('status_active'), color: 'bg-green-500/20 text-green-400 border-green-500/30', icon: CheckCircle },
      paused: { label: p('status_paused'), color: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30', icon: PauseCircle },
      cancelled: { label: p('status_cancelled'), color: 'bg-red-500/20 text-red-400 border-red-500/30', icon: Ban },
    };
    const c = config[status] || { label: status || '-', color: 'bg-gray-500/20 text-gray-400 border-gray-500/30', icon: Clock };
    const Icon = c.icon;
    return (
      <span className={`px-3 py-1.5 rounded-full text-xs font-medium flex items-center gap-1.5 border ${c.color}`}>
        <Icon size={14} />
        {c.label}
      </span>
    );
  };

  const getAutoSendBadge = (autoSend) => {
    if (autoSend) {
      return (
        <span className="px-3 py-1.5 rounded-full text-xs font-medium flex items-center gap-1.5 border bg-purple-500/20 text-purple-400 border-purple-500/30">
          <Send size={14} />
          {p('auto_badge')}
        </span>
      );
    }
    return (
      <span className="px-3 py-1.5 rounded-full text-xs font-medium flex items-center gap-1.5 border bg-blue-500/20 text-blue-400 border-blue-500/30">
        <Users size={14} />
        {p('manual_badge')}
      </span>
    );
  };

  const columnFilterRules = useMemo(() => [
    textColumnRule('recurring_number', (row) => row.recurring_number),
    textColumnRule('client_name', (row) => row.client_name),
    textColumnRule('next_issue_date', (row) => row.next_issue_date),
    textColumnRule('amount', (row) => String(row.amount ?? '')),
  ], []);

  const filteredRecords = useMemo(() => {
    const base = records.filter((record) => {
    if (dateRange.start || dateRange.end) {
      if (!record.next_issue_date) return false;
      const issueDate = new Date(record.next_issue_date);
      if (dateRange.start) {
        const startOfDay = new Date(dateRange.start);
        startOfDay.setHours(0, 0, 0, 0);
        if (issueDate < startOfDay) return false;
      }
      if (dateRange.end) {
        const endOfDay = new Date(dateRange.end);
        endOfDay.setHours(23, 59, 59, 999);
        if (issueDate > endOfDay) return false;
      }
    }

    if (statusFilter !== 'all' && record.status !== statusFilter) return false;

    const matchesQuick = quickFilter === 'all' || record.frequency === quickFilter;

    if (activeDistributionFilter) {
      const freqKeys = ['mensuel', 'trimestriel', 'annuel'];
      const statusKeys = ['active', 'paused', 'cancelled'];
      if (freqKeys.includes(activeDistributionFilter) && record.frequency !== activeDistributionFilter) return false;
      if (statusKeys.includes(activeDistributionFilter) && record.status !== activeDistributionFilter) return false;
    }

    const term = searchTerm.toLowerCase();
    const matchesSearch = !term ||
      record.recurring_number?.toLowerCase().includes(term) ||
      record.client_name?.toLowerCase().includes(term) ||
      record.template_ref?.toLowerCase().includes(term) ||
      record.frequency?.toLowerCase().includes(term);

    return matchesQuick && matchesSearch;
    });
    return applyColumnFilters(base, filters, columnFilterRules);
  }, [records, dateRange, statusFilter, quickFilter, activeDistributionFilter, searchTerm, filters, columnFilterRules]);

  const {
    facturationConfig,
    pageSize: facturationPageSize,
    listPage: facturationListPage,
    setListPage: setFacturationListPage,
    pagedItems,
    totalItems: facturationListTotal,
  } = useFacturationListPagination(filteredRecords);
  const [viewMode, setViewMode] = useFacturationListViewMode(facturationConfig);

  const filteredRecordIds = useMemo(() => filteredRecords.map((r) => r.id), [filteredRecords]);

  const allRowsSelected = filteredRecordIds.length > 0 && filteredRecordIds.every((id) => selectedRecords.includes(id));
  const someRowsSelected = selectedRecords.length > 0 && !allRowsSelected;

  const handleSelectAll = () => {
    if (allRowsSelected || someRowsSelected) {
      setSelectedRecords([]);
    } else {
      setSelectedRecords(filteredRecordIds);
    }
  };

  const handleSelectRecord = (id) => {
    setSelectedRecords((prev) => (prev.includes(id) ? prev.filter((rId) => rId !== id) : [...prev, id]));
  };

  const clientOptions = clients.map((c) => ({
    value: String(c.id),
    label: c.company_name || c.client_number || `Client #${c.id}`,
    isDefault: true
  }));

  const qawaem = isSaudiBrand();

  const listStats = useMemo(() => [
    { key: 'total', title: p('stat_total'), value: stats.total, icon: RefreshCw, color: 'from-blue-500 to-blue-600' },
    { key: 'active', title: p('stat_active'), value: stats.actives, icon: CheckCircle, color: 'from-green-500 to-green-600' },
    { key: 'monthly', title: p('stat_monthly'), value: stats.mensuelles, icon: Calendar, color: 'from-purple-500 to-purple-600' },
    { key: 'quarterly', title: p('stat_quarterly'), value: stats.trimestrielles, icon: Clock, color: 'from-orange-500 to-orange-600' },
    { key: 'revenue', title: p('stat_recurring_revenue'), value: `${stats.caRecurrent} ${getBrandCurrencySuffix()}`, icon: DollarSign, color: 'from-amber-500 to-amber-600', className: 'max-lg:hidden' },
  ], [stats, p]);

  const qawaemStatusOptions = useMemo(() => [
    { value: 'all', label: p('all_statuses') },
    { value: 'active', label: p('dist_active') },
    { value: 'paused', label: p('dist_paused') },
    { value: 'cancelled', label: p('dist_cancelled') },
  ], [p]);

  const qawaemStatusLabel = statusFilter === 'all'
    ? p('status_label')
    : (qawaemStatusOptions.find((o) => o.value === statusFilter)?.label || p('status_label'));

  const qawaemBaseColumns = useMemo(() => ensureStatusBeforeActions([
    { key: 'recurring_number', label: p('col_recurring_number'), required: true },
    { key: 'client_name', label: p('col_client') },
    { key: 'next_issue_date', label: p('col_next_issue') },
    { key: 'amount', label: p('col_amount') },
    { key: 'status', label: p('col_status') },
  ]), [p]);
  const { handleToggleColumn, showCol, pickerColumns, visibleColumnCount } = useQawaemTableColumnVisibility(qawaemBaseColumns);
  const qawaemColumns = useMemo(
    () => withQawaemActionsColumn(pickerColumns, p('col_actions')),
    [pickerColumns, p],
  );
  const visibleTableColCount = useMemo(
    () => countVisibleTableCols(qawaemBaseColumns, showCol, { extra: 2 }),
    [qawaemBaseColumns, showCol],
  );

  const openCreateModal = () => {
    setSelectedRecord(null);
    setFormData({ ...EMPTY_FORM });
    setRecurringFormTab('client');
    setShowModal(true);
  };


  const recurringFormTabs = useMemo(() => ([
    { id: 'client', label: p('section_client'), icon: Building, iconColor: 'text-emerald-400' },
    { id: 'details', label: p('section_amounts'), icon: DollarSign, iconColor: 'text-yellow-400' },
  ]), [p]);

  return (
    <>
    <FacturationListPageLayout
        footer={(
          <FacturationListPaginationBar
            totalItems={facturationListTotal}
            rawPage={facturationListPage}
            onPageChange={setFacturationListPage}
            pageSize={facturationPageSize}
          />
        )}
      title={p('title')}
      subtitle={p('subtitle')}
      icon={RefreshCw}
      stats={listStats}
      viewMode={viewMode}
      onViewModeChange={setViewMode}
      searchTerm={searchTerm}
      onSearchChange={setSearchTerm}
      searchPlaceholder={p('search_placeholder')}
      columns={qawaemColumns}
      visibleColumnCount={visibleColumnCount}
      totalColumnCount={qawaemColumns.length}
      onCreate={openCreateModal}
      createLabel={p('new_recurring')}
      headerActions={(
        <button type="button" onClick={loadRecords} className="refresh-button" title={t('common.refresh')}>
          <RefreshCw size={20} />
        </button>
      )}
      importExport={{
        importOpen: showImportDropdown,
        exportOpen: showExportDropdown,
        onToggleImport: () => { setShowImportDropdown(!showImportDropdown); setShowExportDropdown(false); },
        onToggleExport: () => { setShowExportDropdown(!showExportDropdown); setShowImportDropdown(false); },
        importRef: importDropdownRef,
        exportRef: exportDropdownRef,
        importLabel: p('import_label'),
        exportLabel: p('export_label'),
        generatingLabel: t('common.loading'),
        isGeneratingPDF: false,
        onImportCSV: () => setShowImportDropdown(false),
        onImportExcel: () => setShowImportDropdown(false),
        onExportPDF: () => {},
        onExportExcel: handleExportExcel,
        onExportCSV: handleExportCSV,
      }}
      showAdvancedOptions={showAdvancedOptions}
      onToggleAdvancedOptions={() => setShowAdvancedOptions(!showAdvancedOptions)}
      advancedOptionsLabel={p('advanced_options')}
      advancedOptionsContent={(
        <div className="space-y-6 pt-6">
          <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-2 border border-white/20">
            <div className="flex space-x-1 overflow-x-auto table-scroll">
              {mainTabs.map((tab) => (
                <button key={tab.id} type="button" onClick={() => { setMainViewMode(tab.id); setQuickFilter('all'); setActiveDistributionFilter(null); }} className={`flex-1 flex items-center justify-center space-x-2 px-4 py-3 rounded-2xl font-medium transition-all duration-300 ${mainViewMode === tab.id ? 'bg-gradient-to-r from-blue-500 to-blue-700 text-white shadow-lg shadow-blue-500/30' : 'text-white/80 hover:bg-white/10 hover:text-white'}`}>
                  <tab.icon className="h-5 w-5" />
                  <span className="hidden sm:inline whitespace-nowrap">{tab.name}</span>
                </button>
              ))}
            </div>
          </div>
          {mainViewMode === 'all_recurring' && (
            <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-2 border border-white/20">
              <div className="flex space-x-1 overflow-x-auto table-scroll">
                {quickFilterButtons.map((filter) => (
                  <button key={filter.id} type="button" onClick={() => setQuickFilter(filter.id)} className={`flex-1 flex items-center justify-center space-x-2 px-4 py-3 rounded-2xl font-medium transition-all duration-300 ${quickFilter === filter.id ? 'bg-gradient-to-r from-blue-500 to-blue-700 text-white shadow-lg shadow-blue-500/30' : 'text-white/80 hover:bg-white/10 hover:text-white'}`}>
                    <filter.icon className="h-5 w-5" />
                    <span className="hidden sm:inline whitespace-nowrap">{filter.name}</span>
                  </button>
                ))}
              </div>
            </div>
          )}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-semibold text-white">{distributionInfo.title}</h3>
              {activeDistributionFilter && (
                <button type="button" onClick={() => setActiveDistributionFilter(null)} className="text-xs text-white/60 hover:text-white bg-white/5 hover:bg-white/10 px-3 py-1.5 rounded-lg transition-all flex items-center gap-1">
                  <XCircle size={14} />
                  {p('reset_filter')}
                </button>
              )}
            </div>
            <div {...getErpOverviewGridAttrs(distributionInfo.data.length, '4')}>
              {distributionInfo.data.map((item) => (
                <button key={item.filterKey} type="button" onClick={() => setActiveDistributionFilter(activeDistributionFilter === item.filterKey ? null : item.filterKey)} className={`bg-white/5 rounded-xl p-4 border transition-all cursor-pointer text-left ${activeDistributionFilter === item.filterKey ? 'border-blue-400/60 bg-blue-500/20 shadow-lg shadow-blue-500/20' : 'border-white/10 hover:border-white/20 hover:bg-white/10'}`}>
                  <div className="flex justify-between items-center mb-2">
                    <h4 className={`font-medium ${activeDistributionFilter === item.filterKey ? 'text-white' : 'text-white/80'}`}>{item.name}</h4>
                    <div className={`text-sm rounded-full px-2 py-0.5 ${item.color} text-white force-white font-semibold`}>{item.count}</div>
                  </div>
                  <div className="w-full bg-white/10 h-3 rounded-full overflow-hidden">
                    <div className={`h-3 rounded-full ${item.color} transition-all`} style={{ width: `${(() => { const total = distributionInfo.data.reduce((sum, d) => sum + d.count, 0); return total > 0 ? (item.count / total * 100).toFixed(1) : 0; })()}%` }} />
                  </div>
                </button>
              ))}
            </div>
          </motion.div>
        </div>
      )}
      controlsStrip={(
        <div className="facturation-list-controls-strip p-4 border-b border-white/10">
          <div className="facturation-list-controls-row flex min-w-0 flex-row flex-wrap items-center gap-2 lg:gap-4">
            {viewMode === 'cards' ? (
              <div className="relative min-w-0 flex-1 max-w-md">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-white/50" />
                <input type="text" placeholder={p('search_placeholder')} value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="w-full pl-10 pr-4 py-2 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
            ) : null}
            <div className="facturation-list-toolbar ml-auto flex shrink-0 items-center gap-2 flex-wrap">
              <FacturationViewModeToggle viewMode={viewMode} onViewModeChange={setViewMode} isLight={isDashboardLightMode} />

              {selectedRecords.length > 0 && (
                <button type="button" onClick={handleBulkDelete} className="flex items-center gap-2 px-3 py-2 bg-red-500/20 hover:bg-red-500/30 border border-red-500/30 text-red-200 rounded-lg transition-all text-sm font-medium">
                  <Trash2 size={16} />
                  <span className="hidden sm:inline">{p('delete_count', { count: selectedRecords.length })}</span>
                </button>
              )}

              <div className="relative" ref={statusFilterRef}>
                <button type="button" onClick={() => { setIsStatusFilterOpen(!isStatusFilterOpen); setIsDateRangeOpen(false); }} className="flex items-center gap-2 px-4 py-2 border border-white/20 rounded-lg text-white hover:border-white/30 transition-all bg-white/5">
                  <Filter size={18} className="text-white/80" />
                  <span className="text-white font-medium text-sm">{p('status_label')}</span>
                  <ChevronDown size={14} className={`text-white/70 transition-transform ${isStatusFilterOpen ? 'rotate-180' : ''}`} />
                </button>
                {isStatusFilterOpen && (
                  <div className="border border-blue-400/60 rounded-2xl shadow-2xl min-w-[200px]" style={{ position: 'absolute', top: 'calc(100% + 8px)', right: 0, zIndex: 99999, backgroundImage: 'linear-gradient(to bottom right, rgb(51, 65, 85), rgb(29, 71, 196), rgb(51, 65, 85))' }}>
                    <div className="p-2">
                      {[{ id: 'all', label: p('all_statuses') }, { id: 'active', label: p('dist_active') }, { id: 'paused', label: p('dist_paused') }, { id: 'cancelled', label: p('dist_cancelled') }].map((opt) => (
                        <button key={opt.id} type="button" onClick={() => { setStatusFilter(opt.id); setIsStatusFilterOpen(false); }} className={`w-full px-4 py-2.5 text-left transition-colors flex items-center gap-3 rounded-xl ${statusFilter === opt.id ? 'bg-blue-500/20 text-white' : 'text-white/90 hover:bg-blue-500/20'}`}>
                          <span className="text-sm font-medium">{opt.label}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <div className="relative" ref={dateRangeRef}>
                <button type="button" onClick={() => { setIsDateRangeOpen(!isDateRangeOpen); setIsStatusFilterOpen(false); }} className="flex items-center gap-2 px-4 py-2 border border-white/20 rounded-lg text-white hover:border-white/30 transition-all bg-white/5">
                  <Calendar size={18} className="text-white/80" />
                  <span className="text-white font-medium text-sm">{p('period')}</span>
                  <ChevronDown size={14} className={`text-white/70 transition-transform ${isDateRangeOpen ? 'rotate-180' : ''}`} />
                </button>
                {isDateRangeOpen && (
                  <div className="border border-blue-400/60 rounded-2xl shadow-2xl min-w-[320px]" style={{ position: 'absolute', top: 'calc(100% + 8px)', right: 0, zIndex: 99999, backgroundImage: 'linear-gradient(to bottom right, rgb(51, 65, 85), rgb(29, 71, 196), rgb(51, 65, 85))', overflow: 'visible' }}>
                    <div className="p-4">
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                          <label className="text-white/80 text-xs mb-1.5 block w-full font-medium text-right">{p('start_date')}</label>
                          <CustomDatePicker value={dateRange.start ? (dateRange.start instanceof Date ? dateRange.start.toISOString().split('T')[0] : dateRange.start) : ''} onChange={(dateString) => setDateRange({ ...dateRange, start: dateString ? new Date(dateString) : null })} placeholder={p('select')} />
                        </div>
                        <div>
                          <label className="text-white/80 text-xs mb-1.5 block w-full font-medium text-right">{p('end_date')}</label>
                          <CustomDatePicker value={dateRange.end ? (dateRange.end instanceof Date ? dateRange.end.toISOString().split('T')[0] : dateRange.end) : ''} onChange={(dateString) => setDateRange({ ...dateRange, end: dateString ? new Date(dateString) : null })} placeholder={p('select')} minDate={dateRange.start ? (dateRange.start instanceof Date ? dateRange.start : new Date(dateRange.start)) : undefined} />
                        </div>
                      </div>
                      <div className="flex gap-2 pt-2 border-t border-white/10 mt-3">
                        <button type="button" onClick={() => { setDateRange({ start: null, end: null }); setIsDateRangeOpen(false); }} className="flex-1 px-3 py-2 bg-white/5 hover:bg-white/10 border border-white/20 rounded-xl text-white/80 text-sm font-medium">{p('reset')}</button>
                        <button type="button" onClick={() => setIsDateRangeOpen(false)} className="flex-1 px-3 py-2 bg-blue-500/20 hover:bg-blue-500/30 border border-blue-500/30 rounded-xl text-blue-300 text-sm font-medium">{p('apply')}</button>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <div className="relative z-[100001]" ref={importDropdownRef}>
                <button type="button" onClick={() => { setShowImportDropdown(!showImportDropdown); setShowExportDropdown(false); }} className="bg-orange-500/20 hover:bg-orange-500/30 text-orange-200 border border-orange-500/30 px-3 py-2 rounded-lg transition-all duration-200 flex items-center gap-1.5">
                  <Upload size={16} />
                  <span className="text-sm font-medium">{p('import_label')}</span>
                  <ChevronDown size={14} className={`transition-transform ${showImportDropdown ? 'rotate-180' : ''}`} />
                </button>
                {showImportDropdown && (
                  <div className="border border-blue-400/60 rounded-2xl shadow-2xl min-w-[120px]" style={{ position: 'absolute', top: 'calc(100% + 8px)', right: 0, zIndex: 100002, backgroundImage: 'linear-gradient(to bottom right, rgb(51, 65, 85), rgb(29, 71, 196), rgb(51, 65, 85))' }}>
                    <div className="p-2">
                      <button type="button" onClick={() => setShowImportDropdown(false)} className="w-full px-4 py-2.5 text-left text-white/90 hover:bg-blue-500/20 transition-colors flex items-center gap-3 rounded-xl"><FileText size={16} className="text-red-400" /><span className="text-sm font-medium">{t('common.format_csv')}</span></button>
                      <button type="button" onClick={() => setShowImportDropdown(false)} className="w-full px-4 py-2.5 text-left text-white/90 hover:bg-blue-500/20 transition-colors flex items-center gap-3 rounded-xl"><FileSpreadsheet size={16} className="text-green-400" /><span className="text-sm font-medium">{t('common.format_excel')}</span></button>
                    </div>
                  </div>
                )}
              </div>

              <div className="relative z-[100001]" ref={exportDropdownRef}>
                <button type="button" onClick={() => { setShowExportDropdown(!showExportDropdown); setShowImportDropdown(false); }} className="bg-green-500/20 hover:bg-green-500/30 text-green-200 border border-green-500/30 px-3 py-2 rounded-lg transition-all duration-200 flex items-center gap-1.5">
                  <Download size={16} />
                  <span className="text-sm font-medium">{p('export_label')}</span>
                  <ChevronDown size={14} className={`transition-transform ${showExportDropdown ? 'rotate-180' : ''}`} />
                </button>
                {showExportDropdown && (
                  <div className="border border-blue-400/60 rounded-2xl shadow-2xl min-w-[120px]" style={{ position: 'absolute', top: 'calc(100% + 8px)', right: 0, zIndex: 100002, backgroundImage: 'linear-gradient(to bottom right, rgb(51, 65, 85), rgb(29, 71, 196), rgb(51, 65, 85))' }}>
                    <div className="p-2">
                      <button type="button" onClick={handleExportCSV} className="w-full px-4 py-2.5 text-left text-white/90 hover:bg-blue-500/20 transition-colors flex items-center gap-3 rounded-xl"><FileOutput size={16} className="text-purple-400" /><span className="text-sm font-medium">{t('common.format_csv')}</span></button>
                      <button type="button" onClick={handleExportExcel} className="w-full px-4 py-2.5 text-left text-white/90 hover:bg-blue-500/20 transition-colors flex items-center gap-3 rounded-xl"><FileSpreadsheet size={16} className="text-green-400" /><span className="text-sm font-medium">{t('common.format_excel')}</span></button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
      filterActions={selectedRecords.length > 0 ? (
        <button
          type="button"
          onClick={handleBulkDelete}
          className="flex items-center gap-2 px-3 py-2 rounded-xl border border-red-500/35 bg-red-500/10 text-red-700 hover:bg-red-500/20 transition-colors text-sm font-medium shrink-0"
        >
          <Trash2 size={16} />
          <span>{p('delete_count', { count: selectedRecords.length })}</span>
        </button>
      ) : null}
    >
        {viewMode === 'table' && (
          <div className={facturationTableScrollClass(qawaem)}>
            <table className="w-full employees-table data-table">
              <thead className={facturationTableHeadClass(qawaem)}>
                <tr>
                  <th className={mergeStickySelectClass('px-3 py-3 text-center align-middle')}>
                    <div className="flex justify-center">
                      <QawaemTableCheckbox
                        size="lg"
                        checked={allRowsSelected}
                        indeterminate={someRowsSelected}
                        onChange={handleSelectAll}
                      />
                    </div>
                  </th>
                  {showCol('recurring_number') ? <TableColumnHeaderFilter columnKey="recurring_number" label={p('col_recurring_number')} value={filters.recurring_number || ''} onChange={(v) => setFilter('recurring_number', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white font-semibold py-4" /> : null}
                  {showCol('client_name') ? <TableColumnHeaderFilter columnKey="client_name" label={p('col_client')} value={filters.client_name || ''} onChange={(v) => setFilter('client_name', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white font-semibold py-4" /> : null}
                  <th className="px-6 py-4 text-left text-white font-semibold">{p('col_frequency')}</th>
                  {showCol('next_issue_date') ? <TableColumnHeaderFilter columnKey="next_issue_date" label={p('col_next_issue')} value={filters.next_issue_date || ''} onChange={(v) => setFilter('next_issue_date', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white font-semibold py-4" /> : null}
                  {showCol('amount') ? <TableColumnHeaderFilter columnKey="amount" label={p('col_amount')} value={filters.amount || ''} onChange={(v) => setFilter('amount', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white font-semibold py-4" /> : null}
                  <th className="px-6 py-4 text-center text-white font-semibold">{p('col_send')}</th>
                  {showCol('status') ? <th className="px-6 py-4 text-center text-white font-semibold">{p('col_status')}</th> : null}
                  <QawaemTableHeaderActionsCell
                    label={p('col_actions')}
                    columns={qawaemColumns}
                    onToggle={handleToggleColumn}
                    visibleCount={visibleColumnCount}
                    totalCount={qawaemColumns.length}
                  thClassName="px-6 py-3 text-center text-white font-semibold text-sm whitespace-nowrap align-middle"
                  />
                </tr>
              </thead>
              <tbody className="divide-y divide-white/10 employees-table-body">
                {loading ? (
                  <tr><td colSpan={visibleTableColCount} className="px-6 py-12 text-center text-white/60">{p('loading')}</td></tr>
                ) : filteredRecords.length > 0 ? (
                  pagedItems.map((record, index) => (
                    <tr key={record.id} className={`hover:bg-white/5 transition-colors ${selectedRecords.includes(record.id) ? 'bg-white/10' : ''}`}>
                      <td className={mergeStickySelectClass('px-3 py-4 text-center align-middle')}>
                        <div className="flex justify-center">
                          <QawaemTableCheckbox
                            checked={selectedRecords.includes(record.id)}
                            onChange={() => handleSelectRecord(record.id)}
                          />
                        </div>
                      </td>
                      {showCol('recurring_number') ? (
                      <td className="px-6 py-4">
                        <div className="flex flex-col">
                          <span className="text-white font-semibold">{record.recurring_number || '-'}</span>
                          <span className="text-white/50 text-xs mt-0.5">{record.template_ref || p('no_template')}</span>
                        </div>
                      </td>
                      ) : null}
                      {showCol('client_name') ? (
                      <td className="px-6 py-4">
                        <div className="flex flex-col">
                          <span className="text-white font-semibold">{record.client_name || '-'}</span>
                          <span className="text-white/50 text-xs mt-0.5 flex items-center gap-1"><Building size={12} />{p('client_prefix', { id: record.client_id || '—' })}</span>
                        </div>
                      </td>
                      ) : null}
                      <td className="px-6 py-4">{getFrequencyBadge(record.frequency)}</td>
                      {showCol('next_issue_date') ? (
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-1.5 text-white font-semibold">
                          <Clock size={14} className="text-white/50" />
                          {record.next_issue_date ? new Date(record.next_issue_date).toLocaleDateString(dateLocale) : '-'}
                        </div>
                      </td>
                      ) : null}
                      {showCol('amount') ? (
                      <td className="px-6 py-4">
                        <span className="text-white font-semibold">
                          {parseFloat(record.amount || 0).toLocaleString(dateLocale, { minimumFractionDigits: 2 })} {record.currency || getBrandDefaultCurrency()}
                        </span>
                      </td>
                      ) : null}
                      <td className="px-6 py-4 text-center"><div className="flex justify-center">{getAutoSendBadge(record.auto_send)}</div></td>
                      {showCol('status') ? (
                      <td className="px-6 py-4 text-center"><div className="flex justify-center"><QawaemClickableStatusBadge status={record.status} recordId={record.id} options={statusOptions} onChange={handleStatusChange} title={p('click_change_status')} /></div></td>
                      ) : null}
                      <td className="px-6 py-4">
                        <QawaemErpRowActions
                          onView={() => handleView(record)}
                          onEdit={() => handleEdit(record)}
                          onDelete={() => handleDelete(record.id)}
                          viewTitle={t('common.view')}
                          editTitle={t('common.edit')}
                          deleteTitle={t('common.delete')}
                        />
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr><td colSpan={visibleTableColCount} className="px-6 py-12 text-center text-white/60">{p('empty')}</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {viewMode === 'cards' && (
          <div className="p-4 grid grid-cols-1 gap-4">
            {filteredRecords.length > 0 ? pagedItems.map((record) => (
              <div key={record.id} className="bg-white/5 rounded-2xl p-4 border border-white/10 hover:border-white/20 transition-all h-full min-w-0">
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <RefreshCw size={16} className="text-blue-400" />
                      <span className="text-white font-bold">{record.recurring_number}</span>
                      <QawaemClickableStatusBadge status={record.status} recordId={record.id} options={statusOptions} onChange={handleStatusChange} title={p('click_change_status')} />
                    </div>
                    <p className="text-white/80">{record.client_name}</p>
                    <p className="text-white/50 text-sm mt-1">{getFrequencyBadge(record.frequency)} · {p('card_next_issue')}: {record.next_issue_date ? new Date(record.next_issue_date).toLocaleDateString(dateLocale) : '-'}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-xl font-bold text-white">{parseFloat(record.amount || 0).toLocaleString(dateLocale)} {record.currency || getBrandDefaultCurrency()}</p>
                    <QawaemErpRowActions
                      compact
                      size={14}
                      onView={() => handleView(record)}
                      onEdit={() => handleEdit(record)}
                      onDelete={() => handleDelete(record.id)}
                      viewTitle={p('view')}
                      editTitle={p('edit')}
                      deleteTitle={p('delete')}
                    />
                  </div>
                </div>
              </div>
            )) : (
              <div className="py-12 text-center text-white/60">{p('empty')}</div>
            )}
          </div>
        )}
    </FacturationListPageLayout>

      {showModal && (
        <QawaemErpCrudModal
          open={showModal}
          onClose={resetForm}
          title={selectedRecord ? p('modal_edit') : p('modal_new')}
          icon={RefreshCw}
          formId="recurring-invoice-form"
          saveLabel={selectedRecord ? p('save') : p('create')}
          onSubmit
          testId="recurring-invoice-modal"
          tabs={recurringFormTabs}
          activeTab={recurringFormTab}
          onTabChange={setRecurringFormTab}
        >
              <form id="recurring-invoice-form" onSubmit={handleSubmit}>
                {recurringFormTab === 'client' && (
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                          <div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('form_client')} <span className="qawaem-required-mark">*</span></label>
                            <CustomSelect
                              value={formData.client_id}
                              onChange={(val) => {
                                const client = clients.find((c) => String(c.id) === val);
                                setFormData({ ...formData, client_id: val || '', client_name: client?.company_name || '' });
                              }}
                              options={clientOptions}
                              placeholder={p('placeholder_select_client')}
                              searchable
                            />
                          </div>
                          <div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('form_recurring_number')}</label>
                            <input type="text" value={formData.recurring_number} onChange={(e) => setFormData({ ...formData, recurring_number: e.target.value })} className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder={p('placeholder_recurring_number')} />
                          </div>
                          <div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('form_client_name')}</label>
                            <input type="text" value={formData.client_name} onChange={(e) => setFormData({ ...formData, client_name: e.target.value })} className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder={p('placeholder_client_name')} />
                          </div>
                          <div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('form_frequency')}</label>
                            <CustomSelect value={formData.frequency} onChange={(val) => setFormData({ ...formData, frequency: val })} options={FREQUENCY_OPTIONS} placeholder={p('placeholder_select')} searchable />
                          </div>
                          <div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('form_next_issue')}</label>
                            <CustomDatePicker value={typeof formData.next_issue_date === 'string' ? formData.next_issue_date : ''} onChange={(dateString) => setFormData({ ...formData, next_issue_date: dateString })} placeholder={p('placeholder_select_date')} />
                          </div>
                        </div>
                )}

                {recurringFormTab === 'details' && (
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                          <div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('form_amount')}</label>
                            <CustomNumberInput step={0.01} value={formData.amount} onChange={(v) => setFormData({ ...formData, amount: v })} placeholder={p('placeholder_amount')} />
                          </div>
                          <div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('form_currency')}</label>
                            <CustomSelect value={formData.currency} onChange={(val) => setFormData({ ...formData, currency: val })} options={CURRENCY_OPTIONS} placeholder={p('placeholder_currency')} searchable />
                          </div>
                          <div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('form_template_ref')}</label>
                            <input type="text" value={formData.template_ref} onChange={(e) => setFormData({ ...formData, template_ref: e.target.value })} className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder={p('placeholder_template')} />
                          </div>
                          <div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('form_status')}</label>
                            <CustomSelect value={formData.status} onChange={(val) => setFormData({ ...formData, status: val })} options={STATUS_OPTIONS} placeholder={p('placeholder_status')} searchable />
                          </div>
                          <div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('form_auto_send')}</label>
                            <CustomSelect
                              value={formData.auto_send}
                              onChange={(val) => setFormData({ ...formData, auto_send: val })}
                              options={[
                                { value: '1', label: p('auto_send_yes'), isDefault: true },
                                { value: '', label: p('auto_send_no'), isDefault: true },
                              ]}
                              placeholder={p('placeholder_choose')}
                            />
                          </div>
                          <div className="md:col-span-2">
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('form_notes')}</label>
                            <textarea value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} rows={3} className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none" placeholder={p('placeholder_notes')} />
                          </div>
                        </div>
                )}
              </form>
        </QawaemErpCrudModal>
      )}

      {showViewModal && selectedRecord ? (
      <QawaemErpViewModal
          open
          onClose={() => setShowViewModal(false)}
          title={selectedRecord.recurring_number}
          icon={RefreshCw}
          maxWidth="720px"
          testId="recurring-invoice-view-modal"
        >
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div><p className="text-white/50 text-sm">{p('view_client')}</p><p className="text-white font-semibold">{selectedRecord.client_name}</p></div>
                <div><p className="text-white/50 text-sm">{p('view_frequency')}</p><div className="mt-1">{getFrequencyBadge(selectedRecord.frequency)}</div></div>
                <div><p className="text-white/50 text-sm">{p('view_next_issue')}</p><p className="text-white font-semibold">{selectedRecord.next_issue_date ? new Date(selectedRecord.next_issue_date).toLocaleDateString(dateLocale) : '-'}</p></div>
                <div><p className="text-white/50 text-sm">{p('view_amount')}</p><p className="text-white font-semibold">{parseFloat(selectedRecord.amount || 0).toLocaleString(dateLocale)} {selectedRecord.currency || getBrandDefaultCurrency()}</p></div>
                <div><p className="text-white/50 text-sm">{p('view_template')}</p><p className="text-white">{selectedRecord.template_ref || '-'}</p></div>
                <div><p className="text-white/50 text-sm">{p('view_send')}</p><div className="mt-1">{getAutoSendBadge(selectedRecord.auto_send)}</div></div>
                <div><p className="text-white/50 text-sm">{p('view_status')}</p><div className="mt-1">{getStatusBadge(selectedRecord.status)}</div></div>
              </div>
              {selectedRecord.notes && (
                <div><p className="text-white/50 text-sm mb-1">{p('view_notes')}</p><p className="text-white/80 text-sm">{selectedRecord.notes}</p></div>
              )}
            </div>
        </QawaemErpViewModal>
      ) : null}
    </>
  );
};

export default FacturesRecurrentesPage;
